import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { constructQueryParamName } from '../../../../util/search';
import { stringify } from '../../../../util/urlHelpers';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  IconCard,
  ListingLimitModal,
  Button
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import IconMenu from '../../../../components/IconMenu/IconMenu';
import { FREE_LISTING_LIMIT } from '../../../../util/subscriptionQuotas';

const CreateLisingLink = () => {
  return (
    <NamedLink name="NewListingPage" className={classNames(css.topbarLink, css.newListingLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.createListing" />
      </span>
    </NamedLink>
  );
};


const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={classNames(css.topbarLink, css.signupLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};
const AboutLink = () => {
  return (
    <NamedLink className={css.aboutLink} name="CMSPage" params={{ pageId: 'about' }}>
      <span className={css.topbarCustomLinkLabel}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>
  );
};

const ListingLink = () => {
  return (
    <NamedLink name="SearchPage" className={css.aboutLink}>
      <span className={css.topbarCustomLinkLabel}>
        <FormattedMessage id="TopbarDesktop.browseListings" />
      </span>
    </NamedLink>
  );
};
const CategoryLink = ({ categories, categoryKey }) => {
  return (
    <>
      {categories.length > 0 && (
        <Menu>
          <MenuLabel className={css.categoryMenuLabel} isOpenClassName={css.categoryMenuIsOpen}>
            <span className={css.topbarCustomLinkLabel}>
              <FormattedMessage id="TopbarDesktop.categories" defaultMessage="Categories" />
            </span>
          </MenuLabel>
          <MenuContent className={css.categoryMenuContent}>
            {categories.map((category) => {
              const categoryParamName = constructQueryParamName(`${categoryKey}1`, 'public');
              const searchParams = stringify({
                // [categoryParamName]: category.id,
                [`publicData.${categoryKey}1`]: category.id,
                'publicData.installer_or_listing': 'listing',
                'publicData.listingType': 'component',
              });
              return (
                <MenuItem key={category.id}>
                  <NamedLink
                    className={css.categoryMenuLink}
                    name="SearchPage"
                    to={{ search: searchParams }}
                  >
                    <span className={css.menuItemBorder} />
                    {category.name}
                  </NamedLink>
                </MenuItem>
              );
            })}
          </MenuContent>
        </Menu>
      )}
    </>
  );
};

const ProfileMenu = ({ currentPage, currentUser, inboxTab, onLogout, showManageListingsLink, onManageDisableScrolling }) => {
  const { userType } = currentUser?.attributes?.profile?.publicData || {};

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <>
      <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <IconMenu />
        </MenuLabel>

        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="InboxPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ tab: inboxTab }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.inbox" />
            </NamedLink>
          </MenuItem>
          {showManageListingsLink && userType != 'buyer' ? (
            <MenuItem key="ManageListingsPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            </MenuItem>
          ) : null}
          <MenuItem key="FavoritePage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavoritePage'))}
              name="FavoritePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.favoritesLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    </>
  );
};
const NotLoggedInMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu className={css.notLoggedInProfile}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <svg width="41" height="25" viewBox="0 0 41 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.38379 22.465H38.3838M2.38379 12.465H38.3838M2.38379 2.46497H38.3838" stroke="#232322" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>

        <MenuItem key="NewListingPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
            name="NewListingPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="About Page">
          <br />
        </MenuItem>


      </MenuContent>
    </Menu>
  );
};


/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    onManageDisableScrolling,
  } = props;
  const { categories = [] } = config?.categoryConfiguration || {};
  const categoryKey = config?.categoryConfiguration?.key || 'categoryLevel';

  const [mounted, setMounted] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Listing Limit Logic
  const numberOfListingPosted = currentUser?.attributes?.profile?.publicData?.numberOfListingPosted || 0;
  const subscriptionData = currentUser?.attributes?.profile?.protectedData?.subscription;
  const planNickname = subscriptionData?.nickname ? subscriptionData.nickname.toLowerCase() : '';
  const {amount} = subscriptionData || {};
    let gold = false;
    let premium = false;
    let free = false;
    if (amount === 4900 || amount === 46900) { 
      gold = true;
    } else if (amount === 19900 || amount === 189900) {
      premium = true;
    } else {
      free = true;
    }
  const handleCreateListingClick = e => {
    const isPro = gold;
    const isPremium = premium;

    let limit = FREE_LISTING_LIMIT; // Basic / Free limit

    if (isPremium) {
      limit = Infinity;
    } else if (isPro) {
      limit = 100;
    }

    // Check limit
    if (numberOfListingPosted >= limit) {
      e.preventDefault();
      e.stopPropagation();
      setShowLimitModal(true);
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);
  const { userType } = currentUser?.attributes?.profile?.publicData || {};

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      inboxTab={inboxTab}
      showManageListingsLink={showCreateListingsLink}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const createLisingLinkMaybe = userType === 'buyer' ? null : (
    <div className={css.customListingContainer} onClickCapture={handleCreateListingClick}>
      <CreateLisingLink />
    </div>
  );

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <>
      {/* <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      /> */}
      <div className={css.navigationLinks}>
        <AboutLink />
        <ListingLink />
        <CategoryLink categories={categories} categoryKey={categoryKey} />
      </div>
    </>
  );

  return (
    <>
      {/* PRK-456 State B: listing limit wall. */}
      <ListingLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
      />

      <nav className={classes}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {searchFormMaybe}



        {/* {inboxLinkMaybe} */}
        <div className={css.rightSection}>
        {createLisingLinkMaybe}
        {signupLinkMaybe}
        {profileMenuMaybe}
        </div>
        {/* {loginLinkMaybe} */}
      </nav>
    </>
  );
};

export default TopbarDesktop;
